import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { AuthContext } from '../../../context/AuthContext';
import { getUserCauses, submitElevationRequest, getUserMilestones, awardMilestone } from '../../../firebase/services';
import { BottomNav } from '../../common/BottomNav';
import ElevationCelebrationModal from '../../common/ElevationCelebrationModal';
import MilestoneModal from '../../common/MilestoneModal';

const LEVELS = ['Streetlight', 'Daylight', 'Sunshield'];

const LEVEL_DESCRIPTIONS = {
  Streetlight: 'Personal acts of awareness and care',
  Daylight: 'Collaborative community work',
  Sunshield: 'Strategic systems change advocacy'
};

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 20px 100px;
  min-height: 100vh;
  background: ${theme.colors.background};
`;

const Header = styled.div`
  margin-bottom: 28px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.slateGray};
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: ${theme.colors.deepNavy};
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.headingFont};
  font-size: 24px;
  font-weight: 700;
  color: ${theme.colors.deepNavy};
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 15px;
  color: ${theme.colors.slateGray};
  margin: 0;
  line-height: 1.5;
`;

const Section = styled.div`
  margin-bottom: 28px;
`;

const SectionLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${theme.colors.slateGray};
  margin-bottom: 12px;
`;

const CauseCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 14px;
  border: 2px solid ${props => props.$selected ? theme.colors.brandYellow : '#eee'};
  padding: 16px 18px;
  margin-bottom: 10px;
  cursor: ${props => props.$disabled ? 'default' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.$disabled ? '#eee' : theme.colors.brandYellow};
  }
`;

const CauseTitle = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.deepNavy};
`;

const CauseLevel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 13px;
  color: ${theme.colors.slateGray};
  margin-top: 4px;
`;

const CauseMaxed = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  color: #bbb;
  margin-top: 4px;
  font-style: italic;
`;

const LevelOption = styled.div`
  background: ${theme.colors.white};
  border-radius: 12px;
  border: 2px solid ${props => props.$selected ? theme.colors.brandYellow : '#eee'};
  padding: 14px 16px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${theme.colors.brandYellow};
  }
`;

const LevelTitle = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 15px;
  font-weight: 600;
  color: ${theme.colors.deepNavy};
`;

const LevelDesc = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  color: ${theme.colors.slateGray};
  margin-top: 3px;
`;

const ReflectionArea = styled.textarea`
  width: 100%;
  border-radius: 12px;
  border: 2px solid #eee;
  padding: 14px 16px;
  font-family: ${theme.typography.bodyFont};
  font-size: 15px;
  line-height: 1.5;
  resize: none;
  min-height: 120px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${theme.colors.brandYellow};
  }

  &::placeholder {
    color: #aaa;
  }
`;

const ReflectionHint = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: 12px;
  color: #aaa;
  margin-top: 6px;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, ${theme.colors.brandYellow} 0%, #FFC700 100%);
  color: ${theme.colors.deepNavy};
  border: none;
  border-radius: 14px;
  padding: 16px;
  font-family: ${theme.typography.bodyFont};
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 24px;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(255, 209, 0, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const EmptyMessage = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 14px;
  color: ${theme.colors.slateGray};
`;

const LoadingText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: 15px;
  color: ${theme.colors.slateGray};
`;

const ElevationRequestScreen = () => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [causes, setCauses] = useState([]);
  const [selectedCause, setSelectedCause] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('');
  const [reflection, setReflection] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [celebrationLevel, setCelebrationLevel] = useState(null);
  const [celebrationCause, setCelebrationCause] = useState('');
  const [milestoneToShow, setMilestoneToShow] = useState(null);

  useEffect(() => {
    const fetchCauses = async () => {
      try {
        const userCauses = await getUserCauses(currentUser.uid);
        setCauses(userCauses);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (currentUser) fetchCauses();
  }, [currentUser]);

  // Available levels = only levels ABOVE the current cause level
  const getAvailableLevels = (cause) => {
    if (!cause) return [];
    const currentIndex = LEVELS.indexOf(cause.level.title);
    return LEVELS.slice(currentIndex + 1);
  };

  const handleCauseSelect = (cause) => {
    if (cause.level.title === 'Sunshield') return;
    setSelectedCause(cause);
    setSelectedLevel('');
  };

  const canSubmit = selectedCause && selectedLevel;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    try {
      await submitElevationRequest(
        currentUser.uid,
        selectedCause,
        selectedLevel.toLowerCase(),
        reflection
      );

      // Check for first_elevation milestone
      const earned = await getUserMilestones(currentUser.uid);
      if (!earned.includes('first_elevation')) {
        await awardMilestone(currentUser.uid, 'first_elevation');
        // Show milestone first, then celebration
        setMilestoneToShow('first_elevation');
      } else {
        setCelebrationCause(selectedCause.title);
        setCelebrationLevel(selectedLevel);
      }
    } catch (e) {
      console.error(e);
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleMilestoneDismiss = () => {
    setMilestoneToShow(null);
    setCelebrationCause(selectedCause?.title || '');
    setCelebrationLevel(selectedLevel);
  };

  const handleCelebrationDismiss = () => {
    setCelebrationLevel(null);
    navigate('/profile');
  };

  if (loading) {
    return (
      <Container>
        <LoadingText>{t('common.loading')}</LoadingText>
        <BottomNav />
      </Container>
    );
  }

  const eligibleCauses = causes.filter(c => c.level.title !== 'Sunshield');

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/profile')}>
          {t('elevation.backToProfile')}
        </BackButton>
        <Title>{t('elevation.title')}</Title>
        <Subtitle>
          {t('elevation.subtitle')}
        </Subtitle>
      </Header>

      {/* Step 1: Choose Cause */}
      <Section>
        <SectionLabel>{t('elevation.selectCause')}</SectionLabel>
        {eligibleCauses.length === 0 ? (
          <EmptyMessage>
            {t('elevation.maxLevelMessage')}
          </EmptyMessage>
        ) : (
          causes.map(cause => {
            const isMaxed = cause.level.title === 'Sunshield';
            return (
              <CauseCard
                key={cause.docId}
                $selected={selectedCause?.docId === cause.docId}
                $disabled={isMaxed}
                onClick={() => handleCauseSelect(cause)}
              >
                <CauseTitle>{cause.title}</CauseTitle>
                <CauseLevel>{t('elevation.currentLevel', { level: cause.level.title })}</CauseLevel>
                {isMaxed && <CauseMaxed>{t('elevation.maxLevel')}</CauseMaxed>}
              </CauseCard>
            );
          })
        )}
      </Section>

      {/* Step 2: Choose Target Level */}
      {selectedCause && (
        <Section>
          <SectionLabel>{t('elevation.selectLevel')}</SectionLabel>
          {getAvailableLevels(selectedCause).map(level => (
            <LevelOption
              key={level}
              $selected={selectedLevel === level}
              onClick={() => setSelectedLevel(level)}
            >
              <LevelTitle>{level}</LevelTitle>
              <LevelDesc>{LEVEL_DESCRIPTIONS[level]}</LevelDesc>
            </LevelOption>
          ))}
        </Section>
      )}

      {/* Step 3: Reflection (Optional) */}
      {selectedLevel && (
        <Section>
          <SectionLabel>{t('elevation.reflectionPrompt')}</SectionLabel>
          <ReflectionArea
            placeholder={t('elevation.reflectionPlaceholder')}
            value={reflection}
            onChange={e => setReflection(e.target.value)}
          />
          <ReflectionHint>{t('elevation.reflectionHint')}</ReflectionHint>
        </Section>
      )}

      {/* Submit */}
      {eligibleCauses.length > 0 && (
        <SubmitButton onClick={handleSubmit} disabled={!canSubmit || submitting}>
          {submitting ? t('elevation.submitting') : t('elevation.submit', { level: selectedLevel || '...' })}
        </SubmitButton>
      )}

      {/* Milestone Modal */}
      {milestoneToShow && (
        <MilestoneModal milestoneKey={milestoneToShow} onClose={handleMilestoneDismiss} />
      )}

      {/* Elevation Celebration Modal */}
      {celebrationLevel && (
        <ElevationCelebrationModal
          toLevel={celebrationLevel}
          causeName={celebrationCause}
          onClose={handleCelebrationDismiss}
        />
      )}

      <BottomNav />
    </Container>
  );
};

export default ElevationRequestScreen;
